import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { startOfDay, addDays } from 'date-fns';
import styles from './styles/EvCharging.module.scss';

// Context.
import { OmaLummeApp, EvChargingApp } from '../../omaLummeContext';

// Components.
import Box from '../Common/Box';
import Loading from '../Common/Loading';
import ChargingHistoryTable from './ChargingHistoryTable';

// Actions.
import { fetchEVChargingHistory } from '../../actions';
// Types.
import { TransactionHistoryEventUI, TransactionHistoryRequestUI } from '../../types/evCharging';
// Utils.
import { formatFullDateTime, roundNumber, setPriceFormat } from '../../utils/billing';
import { contractIsEvCharging, formatTag } from '../../utils/evCharging';

const ChargingHistory: React.FunctionComponent = () => {
  const { users, basicData } = OmaLummeApp.useState();
  const { evChargerInfo } = EvChargingApp.useState();
  const { t } = useTranslation();
  const { id } = useParams<'id'>();

  const [loadingHistory, setLoadingHistory] = React.useState<boolean>(false);

  const [transactions, setTransactions] = React.useState<TransactionHistoryEventUI[] | undefined>(undefined);

  const charge = transactions?.find((t) => t.evseTransactionId === id);

  React.useEffect(() => {
    let unMounted = false;

    const evChargingContract = basicData
      ? basicData.filter((d) => contractIsEvCharging(d)).sort((a, b) => (new Date(a.contractStartDate) < new Date(b.contractStartDate) ? -1 : 1))[0]
      : undefined;

    async function getEVChargerInfo(): Promise<void> {
      if (evChargingContract && !unMounted) {
        setLoadingHistory(true);
        const body: TransactionHistoryRequestUI = {
          stopTimeSearchBegin: new Date(evChargingContract.contractStartDate).toISOString(),
          stopTime: startOfDay(addDays(new Date(), 1)).toISOString(),
        };

        const chargingHistoryData = await fetchEVChargingHistory(parseInt(evChargingContract.customerNo), users.activeUser.userId, body);

        if (chargingHistoryData.length > 0) {
          setTransactions(chargingHistoryData);
        } else {
          setTransactions([]);
        }
        setLoadingHistory(false);
      }
    }
    if (!unMounted) {
      getEVChargerInfo();
    }

    return (): void => {
      unMounted = true;
    };
  }, [basicData, users.activeUser, evChargerInfo]);

  return (
    <div>
      <header className={`${styles.evChargingHeader} ${styles.mainHeader}`}>
        <h1 aria-live="polite">{charge ? charge.evseTransactionId : t(`evCharging:history.title`)}</h1>
      </header>
      {!evChargerInfo && <Box>{t(`evCharging:common.noEvContract`)}</Box>}
      {evChargerInfo && charge && (
        <Box size="twoThirds">
          <h2>{charge.groupName}</h2>
          <div className={styles.detail}>
            <h4>{t(`evCharging:history.energyUsed`)}</h4>
            {setPriceFormat(roundNumber(charge.consumptionKWH))} kWh
          </div>

          <div className={styles.detail}>
            <h4>{t(`evCharging:history.chargingPrice`)}</h4>
            {setPriceFormat(roundNumber(charge.totalCostInclVat))} €
          </div>

          <div className={styles.detail}>
            <h4>{t(`evCharging:history.started`)}</h4>
            {formatFullDateTime(new Date(charge.startTimeUTC).valueOf())}
          </div>

          <div className={styles.detail}>
            <h4>{t(`evCharging:history.finished`)}</h4>
            {formatFullDateTime(new Date(charge.endTimeUTC).valueOf())}
          </div>

          <div className={styles.detail}>
            <h4>{t(`evCharging:common.tag`)}</h4>
            {formatTag(charge.rfidTag)}
          </div>

          <div className={styles.detail}>
            <h4>{t(`evCharging:common.charger`)}</h4>
            {`${charge.chargerId}, ${t(`evCharging:common.socket`)} ${charge.socketId}`}
          </div>
        </Box>
      )}
      {!charge && (
        <>
          {loadingHistory && <Loading />}
          {!loadingHistory && transactions && transactions.length === 0 && <Box>{t(`evCharging:history.noChargingHistory`)}</Box>}
          {!loadingHistory && transactions && transactions.length > 0 && <ChargingHistoryTable charges={transactions} />}
        </>
      )}
    </div>
  );
};

export default ChargingHistory;
